import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiFetch } from '../utils/apiFetch'

export const usePromotionsStore = defineStore('promotions', () => {
  const activePromotions = ref([])
  const activeProductPromos = ref({})
  const activePaydayCampaign = ref(null)
  const loading = ref(false)
  const lastFetched = ref(null)
  const lastPromosFetched = ref(null)

  const fetchActivePromotions = async (force = false) => {
    // Cache for 30 seconds unless forced
    if (!force && lastFetched.value && (Date.now() - lastFetched.value < 30000)) {
      return activePromotions.value
    }

    loading.value = true
    try {
      const res = await apiFetch('/api/promotions/public/active')
      const data = await res.json()
      if (data.success) {
        activePromotions.value = data.data || []
        lastFetched.value = Date.now()
      } else {
        activePromotions.value = []
      }
    } catch (e) {
      console.error('Error fetching active promotions in store:', e)
      activePromotions.value = []
    } finally {
      loading.value = false
    }
    return activePromotions.value
  }

  const fetchProductPromos = async (force = false) => {
    if (!force && lastPromosFetched.value && (Date.now() - lastPromosFetched.value < 30000)) {
      return activeProductPromos.value
    }

    try {
      const res = await apiFetch('/api/promotions/public/product-promos')
      const data = await res.json()
      if (data.success) {
        activeProductPromos.value = data.data || {}
        activePaydayCampaign.value = data.payday_campaign || null
        lastPromosFetched.value = Date.now()
      }
    } catch (e) {
      console.error('Error fetching product promos map in store:', e)
    }
    return activeProductPromos.value
  }

  const getProductPromoInfo = (productId) => {
    if (!productId) return null
    return activeProductPromos.value[String(productId)] || null
  }

  const isPaydayActive = computed(() => {
    return !!activePaydayCampaign.value
  })

  const hasActivePromotions = computed(() => {
    return activePromotions.value.length > 0
  })

  // Get active Store Pickup discount (if any)
  const storePickupDiscount = computed(() => {
    const pickupPromo = activePromotions.value.find(p => p.fulfillment_method === 'STORE_PICKUP' || p.fulfillment_method === 'ALL')
    if (pickupPromo && pickupPromo.discount_type === 'PERCENT') {
      return Number(pickupPromo.discount_value || 0)
    }
    return 0
  })

  // Get top active promo for hero/banner
  const topActivePromo = computed(() => {
    if (activePromotions.value.length === 0) return null
    return activePromotions.value.find(p => p.show_in_banner) || activePromotions.value[0]
  })

  return {
    activePromotions,
    activeProductPromos,
    activePaydayCampaign,
    loading,
    fetchActivePromotions,
    fetchProductPromos,
    getProductPromoInfo,
    isPaydayActive,
    hasActivePromotions,
    storePickupDiscount,
    topActivePromo
  }
})
